import { useState, useEffect, useCallback, useMemo } from 'react';
import { Avatar } from '@picsart/design-system/components/Avatar';
import { BaseBottomSheet } from '@picsart/design-system/components/BaseBottomSheet';
import {
  InlineButton,
  ControlSizesMD,
} from '@picsart/design-system/components/InlineButton';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { IconLogout } from '@picsart/design-system/foundation/icons/IconLogout';
import { IconCoins } from '@picsart/design-system/foundation/icons/IconCoins';
import { IconChevronRight } from '@picsart/design-system/foundation/icons/IconChevronRight';
import { IconSettings } from '@picsart/design-system/foundation/icons/IconSettings';
import { IconAccounts } from '@picsart/design-system/foundation/icons/IconAccounts';
import { BadgeTypes } from '@picsart/design-system/components/Badge';
import { getContext } from '@/context';
import { MINIAPP_PACKAGE_ID } from '@/consts/configs';
import { fetchUserCredits } from '@/requests/user/userProfile';
import { logout } from '@/utils/user/authActions';
import { publishSdkEvent } from '@/utils/user/publishSdkEvent';
import useStyles from './styles';

const SHEET_SNAP_AUTHORIZED = [300];
const SHEET_SNAP_GUEST = [150];

const tierToBadge = (
  tierId: string | null | undefined
): BadgeTypes | undefined => {
  if (!tierId) return undefined;
  const id = tierId.toLowerCase();
  if (id.includes('ultra')) return BadgeTypes.Ultra;
  if (id.includes('max')) return BadgeTypes.Max;
  if (id.includes('pro')) return BadgeTypes.Pro;
  if (id.includes('plus')) return BadgeTypes.Plus;
  return undefined;
};

interface UserMenuButtonProps {
  isAuthorized: boolean;
  username: string;
  avatarSrc?: string;
  tierId?: string | null;
  showUserMenu: boolean;
  onCloseApp: () => void;
  onSettingsClick?: () => void;
}

export const UserMenuButton = ({
  isAuthorized,
  username,
  avatarSrc,
  tierId,
  showUserMenu,
  onCloseApp,
  onSettingsClick,
}: UserMenuButtonProps) => {
  const classes = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);

  const snapPoints = useMemo(
    () => (isAuthorized ? SHEET_SNAP_AUTHORIZED : SHEET_SNAP_GUEST),
    [isAuthorized]
  );

  useEffect(() => {
    if (!menuOpen || !isAuthorized) return;
    fetchUserCredits().then((res) => {
      if (res) setCredits((res.credits ?? 0) + (res.addonCredits ?? 0));
    });
  }, [menuOpen, isAuthorized]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const handleLogout = useCallback(async () => {
    setMenuOpen(false);
    await logout();
  }, []);

  const handleOpenCreditsScreen = useCallback(async () => {
    try {
      const result = await getContext().handlers.publishEvent({
        event: 'OpenOfferScreen',
        data: {
          source: MINIAPP_PACKAGE_ID,
          action: MINIAPP_PACKAGE_ID,
          touchpoint: MINIAPP_PACKAGE_ID,
          is_pro_touchpoint: true,
          toolId: 'genai-studio-v2',
        },
      });
      if (result) {
        const res = await fetchUserCredits();
        if (res) setCredits((res.credits ?? 0) + (res.addonCredits ?? 0));
      }
    } catch {
      console.error('[UserMenuButton] Error opening offer screen');
    }
  }, []);

  const handleOpenRegistration = useCallback(() => {
    setMenuOpen(false);
    publishSdkEvent({
      event: 'OpenRegistrationScreen',
      data: {
        source: MINIAPP_PACKAGE_ID,
        action: MINIAPP_PACKAGE_ID,
      },
    });
  }, []);

  if (!showUserMenu) {
    return (
      <InlineButton
        dataTestId="genai-user-menu-close"
        data-testid="genai-user-menu-close"
        size={ControlSizesMD}
        centerIcon={IconCross}
        onClick={onCloseApp}
        aria-label="Close"
        isWithoutBorder
        isWithoutBackground
      />
    );
  }

  const avatarBadge = tierToBadge(tierId);

  return (
    <div className={classes.wrapper}>
      <button
        type="button"
        className={classes.avatarBtn}
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="User menu"
      >
        <Avatar
          size={32}
          imageSrc={isAuthorized ? avatarSrc : undefined}
          badge={isAuthorized ? avatarBadge : undefined}
        />
      </button>

      <BaseBottomSheet
        isOpen={menuOpen}
        onClose={closeMenu}
        snapPoints={snapPoints}
        defaultActiveIndex={0}
        ariaLabel="User menu"
        contentClassName={classes.sheetContent}
      >
        {isAuthorized && (
          <div className={classes.userCard}>
            <Avatar size={44} imageSrc={avatarSrc} badge={avatarBadge} />
            <div className={classes.userInfo}>
              <span className={classes.userName}>{username}</span>
              <span className={classes.userHandle}>@{username}</span>
            </div>
          </div>
        )}

        <div className={classes.menuSection}>
          {isAuthorized && credits !== null && (
            <button
              type="button"
              className={classes.menuItem}
              onClick={handleOpenCreditsScreen}
              data-testid="genai-user-menu-open-credits"
            >
              <IconCoins fill="currentColor" className={classes.menuItemIcon} />
              <span className={classes.menuItemContent}>Credits</span>
              <span className={classes.menuItemEnd}>
                <span className={classes.creditsValue}>
                  {credits.toLocaleString()}
                </span>
                <IconChevronRight fill="currentColor" width={16} height={16} />
              </span>
            </button>
          )}

          {onSettingsClick && (
            <button
              type="button"
              className={classes.menuItem}
              onClick={() => {
                setMenuOpen(false);
                onSettingsClick();
              }}
              data-testid="genai-user-menu-settings"
            >
              <IconSettings
                fill="currentColor"
                className={classes.menuItemIcon}
              />
              <span className={classes.menuItemContent}>Settings</span>
              <span className={classes.menuItemEnd}>
                <IconChevronRight fill="currentColor" width={16} height={16} />
              </span>
            </button>
          )}

          {isAuthorized ? (
            <button
              type="button"
              className={classes.menuItem}
              onClick={handleLogout}
              data-testid="genai-user-menu-logout"
            >
              <IconLogout
                fill="currentColor"
                className={classes.menuItemIcon}
              />
              <span className={classes.menuItemContent}>Log out</span>
            </button>
          ) : (
            <button
              type="button"
              className={classes.menuItem}
              onClick={handleOpenRegistration}
              data-testid="genai-user-menu-sign-in"
            >
              <IconAccounts
                fill="currentColor"
                className={classes.menuItemIcon}
              />
              <span className={classes.menuItemContent}>Sign in</span>
            </button>
          )}
        </div>
      </BaseBottomSheet>
    </div>
  );
};
